/*
 * Dress To AI on dressto.com: the stylist and search by photo.
 * Loaded by snippets/dt-ai.liquid. Answers come from the Dress To app server;
 * product cards, prices (in the shopper's currency) and "Add to bag" use the
 * store's own Shopify endpoints (/products/<handle>.js, /cart/add.js).
 *
 * Open it from anywhere with a link to #dt-stylist or #dt-photo, or any element
 * with data-dt-ai="stylist" | "photo" (optional data-dt-ask="question").
 */
(() => {
  const C = window.DressToAI;
  if (!C || !C.server || window.__dressToAI) return;
  window.__dressToAI = true;

  const API = `${C.server.replace(/\/$/, '')}/api`;
  const ROOT = (C.root || '/').replace(/\/$/, '');
  const SIZES = ['XXS', 'XS', 'S', 'M', 'L', 'XL'];
  const MAX_TURNS = 20;

  // ---------- small helpers ----------

  const session = {
    get(k, d) { try { const v = sessionStorage.getItem(`dtai:${k}`); return v ? JSON.parse(v) : d; } catch { return d; } },
    set(k, v) { try { sessionStorage.setItem(`dtai:${k}`, JSON.stringify(v)); } catch { /* private mode */ } },
  };
  const saved = {
    get(k, d) { try { return localStorage.getItem(`dtai:${k}`) ?? d; } catch { return d; } },
    set(k, v) { try { v ? localStorage.setItem(`dtai:${k}`, v) : localStorage.removeItem(`dtai:${k}`); } catch { /* private mode */ } },
  };

  /** Builds an element. Text is always set as text, never parsed as HTML. */
  function h(tag, attrs = {}, ...children) {
    const el = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs)) {
      if (v === undefined || v === null || v === false) continue;
      if (k === 'class') el.className = v;
      else if (k === 'text') el.textContent = v;
      else if (k === 'html') el.innerHTML = v; // static icons only
      else if (k.startsWith('on')) el.addEventListener(k.slice(2), v);
      else el.setAttribute(k, v === true ? '' : v);
    }
    for (const c of children.flat()) if (c !== null && c !== undefined && c !== false) el.append(c);
    return el;
  }

  const ICON = {
    sparkle: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3l1.8 4.9L18.7 9.7l-4.9 1.8L12 16.4l-1.8-4.9L5.3 9.7l4.9-1.8z" fill="currentColor"/><path d="M18.5 14.5l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8z" fill="currentColor"/></svg>',
    camera: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 8.5A1.5 1.5 0 0 1 5.5 7h2.3l1.4-2h5.6l1.4 2h2.3A1.5 1.5 0 0 1 20 8.5v9a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 17.5z" fill="none" stroke="currentColor" stroke-width="1.4"/><circle cx="12" cy="12.5" r="3.4" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>',
    close: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>',
    send: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 19V5M6 11l6-6 6 6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    back: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 6l-6 6 6 6" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    edit: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 20h4L19 9l-4-4L4 16z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/></svg>',
    image: '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="5" width="16" height="14" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.4"/><circle cx="9" cy="10" r="1.6" fill="currentColor"/><path d="M5 17l4.5-4.5 3 3L15 13l4 4" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/></svg>',
  };

  let moneyFormat;
  function money(cents) {
    try {
      moneyFormat ??= new Intl.NumberFormat(C.locale || 'en', { style: 'currency', currency: C.currency || 'USD' });
      return moneyFormat.format(cents / 100); // Shopify sends prices in hundredths, in the shopper's currency
    } catch {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  function imageUrl(src, width) {
    if (!src) return '';
    const url = src.startsWith('//') ? `https:${src}` : src;
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  }

  function track(name, data = {}) {
    try { window.Shopify?.analytics?.publish?.(`dressto_ai_${name}`, data); } catch { /* analytics never breaks the UI */ }
  }

  async function post(path, body) {
    let res;
    const timer = new AbortController();
    const t = setTimeout(() => timer.abort(), 60_000);
    try {
      res = await fetch(`${API}${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal: timer.signal });
    } catch (e) {
      throw new Error(e?.name === 'AbortError'
        ? 'The stylist is taking too long. Please try again.'
        : "We couldn't reach the stylist. Check your connection and try again.");
    } finally {
      clearTimeout(t);
    }
    if (res.status === 204) return null;
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.error || 'Something went wrong. Please try again.');
    return json;
  }

  const context = () => ({
    channel: 'web',
    country: C.country || undefined,
    currency: C.currency || undefined,
    sizes: state.size ? { letter: state.size } : undefined,
    viewing: C.viewing || undefined,
  });

  // ---------- products (store data, shopper's currency) ----------

  const products = new Map();
  function getProduct(handle) {
    if (!products.has(handle)) {
      products.set(handle, fetch(`${ROOT}/products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null));
    }
    return products.get(handle);
  }

  function sizeOptionIndex(p) {
    const i = (p.options || []).findIndex((o) => /^(sizes?|tamanhos?)$/i.test(o.name || o));
    return i;
  }

  async function addToBag(variantId, button) {
    button.disabled = true;
    const label = button.textContent;
    button.textContent = 'Adding…';
    try {
      const res = await fetch(`${ROOT}/cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.description || json.message || "We couldn't add this piece.");
      const cart = await fetch(`${ROOT}/cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json()).catch(() => null);
      if (cart) {
        // The theme's bag icon and drawer listen for this event (Horizon: ThemeEvents.cartUpdate).
        const ev = new Event('cart:update', { bubbles: true });
        ev.detail = { resource: cart, sourceId: 'dt-ai', data: { source: 'dt-ai', itemCount: cart.item_count } };
        document.dispatchEvent(ev);
      }
      button.textContent = 'Added';
      button.classList.add('is-done');
      toast('Added to your bag', 'View bag', openBag);
      track('add_to_bag', { variantId });
    } catch (e) {
      button.disabled = false;
      button.textContent = label;
      toast(e.message);
    }
  }

  function openBag() {
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') {
      close(() => drawer.open());
    } else {
      window.location.href = C.cartUrl || `${ROOT}/cart`;
    }
  }

  /** A product card. `layout` is "row" (in the chat) or "grid" (photo results). */
  function productCard(handle, layout) {
    const card = h('div', { class: `dtai-card dtai-card--${layout} is-loading` });
    getProduct(handle).then((p) => {
      if (!p) { card.remove(); return; }
      card.classList.remove('is-loading');
      const url = `${ROOT}/products/${p.handle}`;
      const available = (p.variants || []).filter((v) => v.available);
      const sizeAt = sizeOptionIndex(p);
      const sizeOf = (v) => (sizeAt >= 0 ? v.options[sizeAt] : v.public_title || v.title);
      const onSale = p.compare_at_price && p.compare_at_price > p.price;
      const link = h('a', { href: url, class: 'dtai-card__link', onclick: () => track('product_click', { handle }) },
        h('span', { class: 'dtai-card__media' },
          h('img', { src: imageUrl(p.featured_image, layout === 'grid' ? 360 : 300), alt: '', loading: 'lazy', width: 300, height: 400 })),
        h('span', { class: 'dtai-card__title', text: p.title }),
        h('span', { class: 'dtai-card__price' },
          onSale ? h('s', { text: money(p.compare_at_price) }) : null,
          h('span', { text: money(p.price) })));
      card.append(link);

      if (!available.length) {
        card.append(h('span', { class: 'dtai-card__note', text: 'Sold out' }));
        return;
      }
      if (available.length === 1 && (p.variants || []).length === 1) {
        card.append(h('button', { type: 'button', class: 'dtai-card__add', text: 'Add to bag', onclick: (e) => addToBag(available[0].id, e.currentTarget) }));
        return;
      }
      // Sizes in stock; the shopper's size first is pre-selected when available.
      const sizes = h('div', { class: 'dtai-sizes', role: 'group', 'aria-label': `Sizes for ${p.title}` });
      const add = h('button', { type: 'button', class: 'dtai-card__add', text: 'Choose a size', disabled: true });
      let chosen = null;
      const pick = (v, btn) => {
        chosen = v;
        sizes.querySelectorAll('button').forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
        add.disabled = false;
        add.textContent = 'Add to bag';
      };
      for (const v of available) {
        const btn = h('button', { type: 'button', class: 'dtai-size', 'aria-pressed': 'false', text: sizeOf(v), onclick: (e) => pick(v, e.currentTarget) });
        sizes.append(btn);
        if (state.size && sizeOf(v) === state.size) pick(v, btn);
      }
      add.addEventListener('click', () => chosen && addToBag(chosen.id, add));
      card.append(sizes, add);
    });
    return card;
  }

  // ---------- state ----------

  const state = {
    view: 'chat',
    // Kept for this browser tab, so the chat survives moving between pages.
    msgs: savedChat(),
    busy: false,
    size: SIZES.includes(saved.get('size', '')) ? saved.get('size', '') : '',
    photo: null,
  };
  const persist = () => session.set('chat', state.msgs.slice(-MAX_TURNS * 2));

  /** The chat saved in this tab, if it still has the expected shape. */
  function savedChat() {
    const v = session.get('chat', []);
    if (!Array.isArray(v)) return [];
    return v.filter((m) => m && (m.role === 'user' || m.role === 'assistant') && typeof m.content === 'string' && m.content)
      .map((m) => ({ role: m.role, content: m.content.slice(0, 4000), error: m.error === true || undefined,
        products: Array.isArray(m.products) ? m.products.filter((x) => typeof x === 'string' && /^[a-z0-9][a-z0-9-]*$/.test(x)).slice(0, 6) : undefined }));
  }

  // ---------- the sheet ----------

  let dialog; let log; let announcer; let input; let sendBtn; let photoView; let chatView; let newChatBtn; let toastEl; let opener; let pressedOn;
  let closing = null; // while the sheet slides away: { timer, after }
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function build() {
    if (dialog) return;
    newChatBtn = h('button', { type: 'button', class: 'dtai-icon-btn dtai-new', 'aria-label': 'Start a new chat', html: ICON.edit, onclick: newChat, hidden: true });
    const header = h('div', { class: 'dtai-head' },
      h('button', { type: 'button', class: 'dtai-icon-btn dtai-back', 'aria-label': 'Back to the stylist', html: ICON.back, onclick: () => show('chat') }),
      h('div', { class: 'dtai-head__title' },
        h('h2', { id: 'dtai-title', text: 'Stylist' }),
        h('p', { class: 'dtai-sub', id: 'dtai-sub', text: 'AI styling from pieces in stock' })),
      newChatBtn,
      h('button', { type: 'button', class: 'dtai-icon-btn dtai-photo-btn', 'aria-label': 'Search by photo', html: ICON.camera, onclick: () => show('photo') }),
      h('button', { type: 'button', class: 'dtai-icon-btn', 'aria-label': 'Close', html: ICON.close, onclick: close }));

    log = h('div', { class: 'dtai-log' });
    announcer = h('div', { class: 'dtai-sr', 'aria-live': 'polite' });
    input = h('textarea', { class: 'dtai-input', rows: 1, maxlength: 2000, placeholder: 'Where are you going?', 'aria-label': 'Message the stylist', enterkeyhint: 'send' });
    input.addEventListener('input', () => { autoGrow(); sendBtn.disabled = state.busy || !input.value.trim(); });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) { e.preventDefault(); send(input.value); }
    });
    sendBtn = h('button', { type: 'submit', class: 'dtai-send', 'aria-label': 'Send', html: ICON.send, disabled: true });
    const form = h('form', { class: 'dtai-form', onsubmit: (e) => { e.preventDefault(); send(input.value); } }, input, sendBtn);
    const note = h('p', { class: 'dtai-note' },
      'AI stylist powered by Claude. It can make mistakes, so check size and details before you buy. ',
      h('a', { href: C.privacyUrl || '/policies/privacy-policy', text: 'Privacy' }));
    chatView = h('div', { class: 'dtai-view dtai-view--chat' }, log, h('div', { class: 'dtai-compose' }, form, note));

    photoView = h('div', { class: 'dtai-view dtai-view--photo', hidden: true });
    toastEl = h('div', { class: 'dtai-toast', role: 'status', hidden: true });

    dialog = h('dialog', { class: 'dtai', 'aria-labelledby': 'dtai-title' }, h('div', { class: 'dtai-sheet' }, header, chatView, photoView, toastEl, announcer));
    dialog.addEventListener('close', onClosed);
    // Escape slides the sheet away like the close button, instead of making it vanish.
    dialog.addEventListener('cancel', (e) => { e.preventDefault(); close(); });
    dialog.addEventListener('animationend', (e) => {
      if (e.target === dialog && /^dtai-(sheet|panel)-out$/.test(e.animationName)) finishClose();
    });
    // Click outside the sheet (desktop) closes it, but not a text selection that ends there.
    dialog.addEventListener('pointerdown', (e) => { pressedOn = e.target; });
    dialog.addEventListener('click', (e) => { if (e.target === dialog && pressedOn === dialog) close(); });
    document.body.append(dialog);
    renderChat();
  }

  function autoGrow() {
    input.style.height = 'auto';
    input.style.height = `${Math.min(input.scrollHeight, 120)}px`;
  }

  // Phones: keep the header and message box visible when the keyboard opens.
  function fitToViewport() {
    const vv = window.visualViewport;
    if (!dialog?.open || !vv || window.matchMedia('(min-width: 750px)').matches) { dialog?.style.removeProperty('height'); return; }
    dialog.style.height = `${Math.round(vv.height)}px`;
    if (vv.offsetTop) window.scrollTo(0, 0);
  }
  window.visualViewport?.addEventListener('resize', fitToViewport);

  function open(view = 'chat', ask) {
    build();
    cancelClose();
    if (!dialog.open) {
      opener = document.activeElement;
      closeThemeSearch();
      dialog.showModal();
      fitToViewport();
      document.documentElement.classList.add('dtai-lock');
      track('open', { view, template: C.template });
    }
    show(view);
    if (ask) send(ask);
    else if (view === 'chat' && window.matchMedia('(min-width: 750px)').matches) input.focus();
  }

  /** Slides the sheet away, then closes it. `after` runs once it's gone (e.g. opening the bag). */
  function close(after) {
    const then = typeof after === 'function' ? after : null;
    if (!dialog?.open) { then?.(); return; }
    if (closing) { if (then) closing.after = then; return; }
    if (reduceMotion.matches) { dialog.close(); then?.(); return; }
    closing = { after: then, timer: setTimeout(finishClose, 500) }; // in case the animation can't run
    dialog.classList.add('is-closing');
  }

  function finishClose() {
    if (!closing) return;
    const { after, timer } = closing;
    clearTimeout(timer);
    closing = null;
    dialog.classList.remove('is-closing');
    dialog.close();
    after?.();
  }

  /** Opening again while it slides away keeps it open. */
  function cancelClose() {
    if (!closing) return;
    clearTimeout(closing.timer);
    closing = null;
    dialog.classList.remove('is-closing');
  }

  function onClosed() {
    if (closing) { clearTimeout(closing.timer); closing = null; dialog.classList.remove('is-closing'); }
    document.documentElement.classList.remove('dtai-lock');
    if (/#dt-(stylist|photo)$/.test(location.hash)) history.replaceState(history.state, '', location.pathname + location.search);
    opener?.focus?.();
  }

  function show(view) {
    state.view = view;
    chatView.hidden = view !== 'chat';
    photoView.hidden = view !== 'photo';
    dialog.classList.toggle('is-photo', view === 'photo');
    dialog.querySelector('#dtai-title').textContent = view === 'photo' ? 'Search by photo' : 'Stylist';
    dialog.querySelector('#dtai-sub').textContent = view === 'photo' ? 'Find similar pieces at Dress To' : 'AI styling from pieces in stock';
    if (view === 'photo') renderPhoto();
    else scrollDown();
  }

  function toast(text, actionLabel, action) {
    toastEl.replaceChildren(h('span', { text }), actionLabel ? h('button', { type: 'button', text: actionLabel, onclick: action }) : null);
    toastEl.hidden = false;
    clearTimeout(toast.t);
    toast.t = setTimeout(() => { toastEl.hidden = true; }, 7000);
  }

  // ---------- chat ----------

  function suggestions() {
    if (C.viewing) return ['What goes with this?', 'Is this true to size?', 'Where can I wear this?'];
    const budget = C.currency === 'USD' || !C.currency ? 'Wedding guest look under $250' : 'Wedding guest look';
    return [budget, 'A weekend in Miami', 'Office to dinner', "What's new for fall?"];
  }

  function intro() {
    const sizeRow = h('div', { class: 'dtai-size-pick', role: 'group', 'aria-label': 'Your size (optional)' },
      h('span', { text: 'Your size' }),
      SIZES.map((s) => h('button', {
        type: 'button', class: 'dtai-size', 'aria-pressed': String(state.size === s), text: s,
        onclick: (e) => {
          state.size = state.size === s ? '' : s;
          saved.set('size', state.size);
          e.currentTarget.parentElement.querySelectorAll('button').forEach((b) => b.setAttribute('aria-pressed', String(b.textContent === state.size)));
        },
      })));
    return h('div', { class: 'dtai-intro' },
      h('p', { class: 'dtai-hello', text: C.viewing
        ? `Oi! Ask me anything about the ${C.viewing}: fit, what to wear it with, or where to wear it.`
        : "Oi! I'm your Dress To stylist. Tell me where you're going and I'll put a look together from pieces in stock." }),
      h('div', { class: 'dtai-chips' }, suggestions().map((s) => h('button', { type: 'button', class: 'dtai-chip', text: s, onclick: () => send(s) }))),
      sizeRow,
      h('button', { type: 'button', class: 'dtai-photo-cta', onclick: () => show('photo') },
        h('span', { class: 'dtai-photo-cta__icon', html: ICON.camera }),
        h('span', {}, h('b', { text: 'Seen a look you love?' }), h('span', { text: 'Search by photo' }))));
  }

  function bubble(m, i) {
    if (m.role === 'user') return h('div', { class: 'dtai-msg dtai-msg--me' }, h('p', { text: m.content }));
    const wrap = h('div', { class: 'dtai-msg dtai-msg--ai' },
      h('span', { class: 'dtai-msg__who', html: `${ICON.sparkle}<span>Stylist</span>` }),
      h('p', { text: m.content }));
    if (m.products?.length) {
      wrap.append(h('div', { class: 'dtai-row', role: 'list' }, m.products.map((handle) => {
        const c = productCard(handle, 'row');
        c.setAttribute('role', 'listitem');
        return c;
      })));
    }
    if (!m.error) wrap.append(reportLink('stylist', () => [m.content, ...(m.products || []).map((p) => `• ${p}`)].join('\n'), state.msgs[i - 1]?.content));
    return wrap;
  }

  function renderChat() {
    log.replaceChildren();
    if (!state.msgs.length) log.append(intro());
    state.msgs.forEach((m, i) => log.append(bubble(m, i)));
    newChatBtn.hidden = !state.msgs.length;
    if (state.busy) log.append(typing());
    scrollDown();
  }

  /** Adds one message without rebuilding the chat (keeps chosen sizes and "Added" states). */
  function appendMessage(i) {
    log.querySelector('.dtai-intro')?.remove();
    log.querySelector('.dtai-typing')?.remove();
    const el = bubble(state.msgs[i], i);
    log.append(el);
    newChatBtn.hidden = false;
    return el;
  }

  function typing() {
    return h('div', { class: 'dtai-typing', role: 'status' }, h('span', { class: 'dtai-dots', 'aria-hidden': 'true' }, h('i'), h('i'), h('i')), h('span', { text: 'Styling your look…' }));
  }

  function scrollDown() {
    requestAnimationFrame(() => { if (log) log.scrollTop = log.scrollHeight; });
  }

  function newChat() {
    if (state.busy) return;
    state.msgs = [];
    persist();
    renderChat();
    input.focus();
  }

  async function send(text) {
    const t = String(text || '').trim().slice(0, 2000);
    if (!t || state.busy) return;
    if (!dialog?.open) { open('chat', t); return; }
    show('chat');
    const typedHere = document.activeElement === input || document.activeElement === sendBtn;
    input.value = '';
    autoGrow();
    state.msgs.push({ role: 'user', content: t });
    persist(); // the question survives a page change even before the answer arrives
    state.busy = true;
    sendBtn.disabled = true;
    appendMessage(state.msgs.length - 1);
    log.append(typing());
    scrollDown();
    track('message', { template: C.template });
    try {
      const history = state.msgs.filter((m) => !m.error).slice(-MAX_TURNS)
        .map(({ role, content }) => ({ role, content: content.slice(0, role === 'user' ? 2000 : 4000) }));
      while (history.length && history[0].role !== 'user') history.shift();
      const res = await post('/ai/stylist', { messages: history, context: context() });
      if (!res || typeof res.reply !== 'string' || !res.reply.trim()) throw new Error('Something went wrong. Please try again.');
      state.msgs.push({ role: 'assistant', content: res.reply, products: (res.products || []).map((p) => p?.handle).filter((x) => typeof x === 'string') });
    } catch (e) {
      state.msgs.push({ role: 'assistant', content: e.message, error: true });
    } finally {
      state.busy = false;
      persist();
      const el = appendMessage(state.msgs.length - 1);
      announcer.textContent = state.msgs.at(-1).content;
      scrollDown();
      sendBtn.disabled = !input.value.trim();
      // Keep focus where the shopper is: the message box on computers, the new reply on phones.
      if (typedHere && !window.matchMedia('(pointer: coarse)').matches) input.focus();
      else if (dialog.contains(document.activeElement) === false || document.activeElement === document.body) {
        el.setAttribute('tabindex', '-1');
        el.focus({ preventScroll: true });
      }
    }
  }

  // ---------- report (shoppers can flag a reply) ----------

  function reportLink(feature, content, prompt) {
    const box = h('div', { class: 'dtai-report' });
    const done = (msg) => box.replaceChildren(h('span', { text: msg }));
    const choose = () => box.replaceChildren(
      h('span', { text: 'What was wrong?' }),
      h('button', { type: 'button', text: 'Wrong or misleading', onclick: () => report('wrong') }),
      h('button', { type: 'button', text: 'Offensive or harmful', onclick: () => report('offensive') }));
    const report = (reason) => post('/ai/report', { feature, reason, content: content().slice(0, 4000), prompt: prompt?.slice(0, 2000) })
      .then(() => done("Thanks, we'll review it."), () => done("Couldn't send the report. Please try again later."));
    box.append(h('button', { type: 'button', class: 'dtai-report__open', text: 'Report', onclick: choose }));
    return box;
  }

  // ---------- search by photo ----------

  function renderPhoto() {
    const p = state.photo;
    if (!p) {
      const camera = h('input', { type: 'file', accept: 'image/*', capture: 'environment', hidden: true });
      const library = h('input', { type: 'file', accept: 'image/*', hidden: true });
      [camera, library].forEach((inp) => inp.addEventListener('change', () => inp.files?.[0] && photoSearch(inp.files[0])));
      const drop = h('div', { class: 'dtai-drop' },
        h('span', { class: 'dtai-drop__icon', html: ICON.image }),
        h('p', { class: 'dtai-drop__title', text: 'Seen a look you love?' }),
        h('p', { text: "Take or choose a photo and we'll find similar Dress To pieces in stock." }));
      drop.addEventListener('dragover', (e) => { e.preventDefault(); drop.classList.add('is-over'); });
      drop.addEventListener('dragleave', () => drop.classList.remove('is-over'));
      drop.addEventListener('drop', (e) => { e.preventDefault(); drop.classList.remove('is-over'); const f = e.dataTransfer?.files?.[0]; if (f) photoSearch(f); });
      const touch = window.matchMedia('(pointer: coarse)').matches;
      photoView.replaceChildren(h('div', { class: 'dtai-photo' },
        drop,
        touch ? h('button', { type: 'button', class: 'dtai-btn', onclick: () => camera.click() }, h('span', { html: ICON.camera }), 'Take a photo') : null,
        h('button', { type: 'button', class: `dtai-btn${touch ? ' dtai-btn--outline' : ''}`, onclick: () => library.click() }, h('span', { html: ICON.image }), touch ? 'Choose from your photos' : 'Upload a photo'),
        h('p', { class: 'dtai-note', text: 'Your photo is used only for this search and is not kept. Photos are analysed by Claude, our AI provider.' }),
        camera, library));
      return;
    }
    const head = h('div', { class: 'dtai-photo-head' },
      h('img', { src: p.preview, alt: 'Your photo', class: 'dtai-photo-head__img' }),
      h('div', {},
        h('p', { class: 'dtai-eyebrow', text: p.loading ? 'Looking at your photo…' : 'We spotted' }),
        p.loading
          ? h('span', { class: 'dtai-dots', 'aria-hidden': 'true' }, h('i'), h('i'), h('i'))
          : h('div', { class: 'dtai-tags' }, (p.attributes || []).map((a) => h('span', { class: 'dtai-tag', text: a })))));
    const body = [head];
    if (p.error) body.push(h('p', { class: 'dtai-error', role: 'alert', text: p.error }));
    if (!p.loading && !p.error) {
      if (p.handles.length) {
        body.push(h('h3', { class: 'dtai-h3', text: 'Similar at Dress To' }), h('div', { class: 'dtai-grid' }, p.handles.map((x) => productCard(x, 'grid'))));
      } else {
        body.push(h('p', { class: 'dtai-muted', text: 'No close match in stock right now. Try another photo, or ask the stylist for something similar.' }));
      }
      body.push(h('button', {
        type: 'button', class: 'dtai-btn dtai-btn--outline',
        text: 'Ask the stylist about this look',
        onclick: () => send(`I'm looking for something like this: ${(p.attributes || []).join(', ')}.`),
      }));
      body.push(reportLink('photo', () => [`We spotted: ${(p.attributes || []).join(', ')}`, ...p.handles.map((x) => `• ${x}`)].join('\n')));
    }
    if (!p.loading) body.push(h('button', { type: 'button', class: 'dtai-btn dtai-btn--text', text: 'Try another photo', onclick: () => { state.photo = null; renderPhoto(); } }));
    photoView.replaceChildren(h('div', { class: 'dtai-photo' }, body));
  }

  /** Resizes the photo in the browser (longest side 1024 px, JPEG): faster upload, lower AI cost. */
  async function shrink(file) {
    const url = URL.createObjectURL(file);
    try {
      const img = await new Promise((resolve, reject) => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.onerror = () => reject(new Error('unreadable'));
        i.src = url;
      });
      const scale = Math.min(1, 1024 / Math.max(img.naturalWidth, img.naturalHeight));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
      canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/jpeg', 0.82);
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  async function photoSearch(file) {
    let dataUrl;
    try {
      dataUrl = await shrink(file);
    } catch {
      state.photo = { preview: '', loading: false, error: "We couldn't open that photo. Please choose a JPG or PNG.", handles: [], attributes: [] };
      renderPhoto();
      return;
    }
    state.photo = { preview: dataUrl, loading: true, handles: [], attributes: [] };
    renderPhoto();
    track('photo_search', { template: C.template });
    try {
      const res = await post('/ai/visual-search', { imageBase64: dataUrl.split(',')[1], mediaType: 'image/jpeg', context: context() });
      Object.assign(state.photo, { loading: false, attributes: res.attributes || [], handles: (res.products || []).map((x) => x.handle).filter(Boolean) });
    } catch (e) {
      Object.assign(state.photo, { loading: false, error: e.message });
    }
    if (state.view === 'photo') renderPhoto();
  }

  // ---------- entry points on the page ----------

  function launcher() {
    if (C.template === 'cart' || document.querySelector('.dtai-launch')) return;
    const btn = h('button', { type: 'button', class: `dtai-launch${C.template === 'product' ? ' dtai-launch--raised' : ''}`, 'aria-label': 'Open the Dress To stylist', onclick: () => open('chat') },
      h('span', { html: ICON.sparkle }), h('span', { class: 'dtai-launch__label', text: 'Stylist' }));
    document.body.append(btn);
  }

  /** The theme's search panel: add "Search by photo" and "Ask the stylist" under the search box. */
  function enhanceSearch(root = document) {
    root.querySelectorAll('.predictive-search-form__header').forEach((header) => {
      if (header.nextElementSibling?.classList.contains('dtai-search-tools')) return;
      const field = header.querySelector('input[type="search"]');
      const ask = h('button', { type: 'button', class: 'dtai-search-tool', onclick: () => open('chat', field?.value.trim() || undefined) },
        h('span', { html: ICON.sparkle }), h('span', { class: 'dtai-search-tool__label', text: 'Ask the stylist' }));
      const tools = h('div', { class: 'dtai-search-tools' },
        ask,
        h('button', { type: 'button', class: 'dtai-search-tool', onclick: () => open('photo') }, h('span', { html: ICON.camera }), h('span', { text: 'Search by photo' })));
      field?.addEventListener('input', () => {
        const q = field.value.trim();
        ask.querySelector('.dtai-search-tool__label').textContent = q ? `Ask the stylist: “${q.length > 28 ? `${q.slice(0, 27)}…` : q}”` : 'Ask the stylist';
      });
      header.after(tools);
    });
  }

  function closeThemeSearch() {
    const modal = document.querySelector('dialog-component#search-modal');
    try { modal?.closeDialog?.(); } catch { /* the theme closes it some other way */ }
  }

  /** Search results page: offer the stylist for searches the shop search can't answer well. */
  function searchBanner() {
    // The search words come from the address bar and are only ever shown as text.
    const terms = (new URLSearchParams(location.search).get('q') || '').trim().slice(0, 300);
    if (C.template !== 'search' || !terms || document.querySelector('.dtai-search-banner')) return;
    const anchor = document.querySelector('main .search-page-input, main form[action$="/search"]') || document.querySelector('main h1');
    if (!anchor) return;
    const banner = h('div', { class: 'dtai-search-banner' },
      h('span', { html: ICON.sparkle }),
      h('p', {}, h('b', { text: 'Not quite it? ' }), 'Ask our stylist and get a look from pieces in stock.'),
      h('button', { type: 'button', class: 'dtai-chip', text: 'Ask the stylist', onclick: () => open('chat', terms) }));
    (anchor.closest('form') || anchor).after(banner);
  }

  document.addEventListener('click', (e) => {
    const el = e.target.closest('[data-dt-ai], a[href$="#dt-stylist"], a[href$="#dt-photo"]');
    if (!el) return;
    e.preventDefault();
    const photo = el.dataset.dtAi === 'photo' || /#dt-photo$/.test(el.getAttribute('href') || '');
    open(photo ? 'photo' : 'chat', el.dataset.dtAsk || undefined);
  });

  function start() {
    launcher();
    enhanceSearch();
    searchBanner();
    if (location.hash === '#dt-stylist') open('chat');
    if (location.hash === '#dt-photo') open('photo');
    // The theme may render the search panel later (and re-render sections in the editor).
    new MutationObserver((records) => {
      for (const r of records) for (const n of r.addedNodes) if (n.nodeType === 1 && (n.matches?.('.predictive-search-form__header') || n.querySelector?.('.predictive-search-form__header'))) enhanceSearch(n.parentElement || document);
    }).observe(document.body, { childList: true, subtree: true });
  }

  window.addEventListener('hashchange', () => {
    if (location.hash === '#dt-stylist') open('chat');
    if (location.hash === '#dt-photo') open('photo');
  });

  window.DressToAI.open = open;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
